// Context Imports
import { OrderActions } from '@/lib/context/orderActions';

// Function Imports
import { addAuthorizationHeader } from '@/lib/functions/addAuthorizationHeader';
import { calculateTotalQuantity } from '@/lib/functions/calculateTotalQuantity';
import { getOrderToken } from '@/lib/functions/getOrderToken';
import { getSession } from 'next-auth/react'; 
import { updateOrder } from '@/lib/context/orderActions';

// Type Imports
import { OrderState as OrderStateType } from '@/lib/types/orderState';
import { OrderPayload as OrderPayloadType } from '@/lib/types/orderPayload';

/**
 * Retrieves the order contents.
 *
 * @param {OrderStateType} orderState - The current state of the order.
 * @param {React.Dispatch<OrderActions>} orderDispatch - The dispatch function for order actions.
 * @returns {Promise<OrderPayloadType | null>} - The updated order, if successful, or an error message.
 */
export async function getOrderByToken(
  orderState?: OrderStateType,
  orderDispatch?: React.Dispatch<OrderActions>,
  tokenValue = '',
): Promise<OrderPayloadType | null> {

  let token = null;

  if (tokenValue && tokenValue.trim() !== '') {
    token = tokenValue;
  } else if (orderState && orderDispatch) {
    const tokenResp = await getOrderToken(orderState, orderDispatch);
    token = tokenResp.token;
  }

  if (!token) {
    console.error('Error in GET /api/orders: No order token available');
    return null;
  }

  // Get the session to check if the user is logged in
  const session = await getSession();

  // Prepare headers for the request
  const headers = addAuthorizationHeader(
    { 'Content-Type': 'application/ld+json' },
    orderState?.tokenType == 'customer' ? session?.token : null,
  );

  try {
    const getCartAPIResponse = await fetch(`${process.env.NEXT_PUBLIC_SITE_URL}/api/order-by-token/${token}/`, {
      method: 'GET',
      headers,
      cache: 'no-cache',
    });

    const orderObject = await getCartAPIResponse.json();

    const itemCount = orderObject?.totalItemQuantityInCart ?? calculateTotalQuantity(orderObject.items);
    const orderStateObject: OrderStateType = {
      itemCount: itemCount,
      token: orderObject.token || null,
      tokenType: orderState?.tokenType || 'guest',
    };

    if (orderDispatch) {
      updateOrder(orderDispatch, orderStateObject);
    }

    return orderObject;
  } catch {
    console.error('Error in GET /api/orders');
    return null;
  }
}
